import {Session} from "../../domain/session.entity";
import {httpClientV2} from "../../../../infrastructure/http";
import {SessionMapperV2} from "../mappers/session.mapper.v2";
import {
  V2SessionResponse,
  V2SessionCustomerResponse,
  CreateSessionData,
  UpdateSessionData,
  PaginationParams,
  V2PaginatedResult,
  SessionSalesInsights,
  SessionCustomer,
} from "../mappers/session.mapper.types";
import {SessionRepository} from "./session.repository";

/**
 * V2 API response wrapper for single session.
 */
interface V2GetSessionResponse {
  data: V2SessionResponse;
}

/**
 * V2 API response wrapper for session list.
 * V2 API returns nested structure: { data: { data: [...], total, hasNextPage } }
 */
interface V2GetListResponse {
  data: {
    data: V2SessionResponse[];
    total: number;
    hasNextPage: boolean;
  };
}

/**
 * V2 API response for session sales insights.
 * The API returns a currency-keyed revenue map and a totalSales count.
 */
interface V2SalesInsightsResponse {
  data: {
    totalSales: number;
    totalRevenueByCurrency: Record<string, number>;
  };
}

/**
 * V2 API response for session customers list.
 * Uses same nested pattern as getList: { data: { data: [...], total, hasNextPage } }
 */
interface V2CustomersResponse {
  data: {
    data: V2SessionCustomerResponse[];
    total: number;
    hasNextPage: boolean;
  };
}

/**
 * V2 API implementation of SessionRepository.
 *
 * Key differences from V1:
 * - Uses httpClientV2 (different base URL)
 * - V2 endpoints: booking/services/{id} (not service/id/{id})
 * - Uses PATCH for updates (not PUT)
 * - Uses perPage in query params (not per_page)
 *
 * Pure TypeScript - no React imports.
 */
export class SessionRepositoryV2 implements SessionRepository {
  async getById(id: number): Promise<Session> {
    const response = await httpClientV2.get<V2GetSessionResponse>(
      `booking/services/${id}`
    );

    return SessionMapperV2.toDomain(response.data);
  }

  async create(data: CreateSessionData): Promise<Session> {
    const payload = SessionMapperV2.toCreatePayload(data);
    const response = await httpClientV2.post<V2GetSessionResponse>(
      "booking/services",
      payload
    );

    return SessionMapperV2.toDomain(response.data);
  }

  async update(data: UpdateSessionData): Promise<Session> {
    const payload = SessionMapperV2.toUpdatePayload(data);
    const response = await httpClientV2.patch<V2GetSessionResponse>(
      `booking/services/${data.id}`,
      payload
    );

    return SessionMapperV2.toDomain(response.data);
  }

  async delete(id: number): Promise<void> {
    await httpClientV2.delete(`booking/services/${id}`);
  }

  async getList(params: PaginationParams): Promise<V2PaginatedResult<Session>> {
    // V2 API uses camelCase: perPage (not per_page like V1)
    // V2 returns nested structure: { data: { data: [...], total, hasNextPage } }
    const response = await httpClientV2.get<V2GetListResponse>(
      `booking/services?page=${params.page}&perPage=${params.perPage}`
    );

    return {
      data: SessionMapperV2.toDomainList(response.data.data),
      total: response.data.total,
      hasNextPage: response.data.hasNextPage,
    };
  }

  /**
   * Get sales insights for a session (V2-only feature).
   */
  async getSalesInsights(id: number): Promise<SessionSalesInsights> {
    const response = await httpClientV2.get<V2SalesInsightsResponse>(
      `booking/services/${id}/sales-insights`
    );

    return {
      totalBookings: response.data.totalSales ?? 0,
      totalRevenue: Object.entries(response.data.totalRevenueByCurrency ?? {}).map(
        ([currency, totalRevenue]) => ({currency, totalRevenue})
      ),
    };
  }

  /**
   * Get customers who booked a session (V2-only feature).
   */
  async getCustomers(
    id: number,
    params: PaginationParams
  ): Promise<V2PaginatedResult<SessionCustomer>> {
    const response = await httpClientV2.get<V2CustomersResponse>(
      `booking/services/${id}/customers?page=${params.page}&perPage=${params.perPage}`
    );

    return {
      data: (response.data.data ?? []).map((row) => SessionMapperV2.toCustomer(row)),
      total: response.data.total ?? 0,
      hasNextPage: response.data.hasNextPage ?? false,
    };
  }

  /**
   * Export customers who booked a session as CSV (V2-only feature).
   * Returns a Blob that can be downloaded.
   */
  async exportCustomersCSV(id: number): Promise<Blob> {
    const response = await httpClientV2.get<Blob>(
      `booking/services/${id}/customers/export`,
      {responseType: "blob"}
    );

    return response;
  }
}
